// Shared feedback banner for the admin panel.
//
// A modern, rounded alert with an Ionicons leading icon, used to surface success /
// error / info / warning feedback consistently across every admin screen. It lives in
// `components/` (not the Products subfolder) so any admin screen can import it without
// reaching into a feature folder. Visual style mirrors `InfoBanner` in
// `src/pages/AdminPanel/Products/formStyles.js`, built only on RN primitives so it works
// on Expo Web.
//
// Colors come exclusively from ADMIN_COLORS and the font from ADMIN_FONT — never the
// client's brand colors/font.

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';

const VARIANTS = {
  success: {
    bg: ADMIN_COLORS.successSoft,
    border: ADMIN_COLORS.successBorder,
    text: ADMIN_COLORS.success,
    icon: 'checkmark-circle-outline',
  },
  error: {
    bg: ADMIN_COLORS.dangerSoft,
    border: ADMIN_COLORS.dangerBorder,
    text: ADMIN_COLORS.danger,
    icon: 'alert-circle-outline',
  },
  info: {
    bg: ADMIN_COLORS.infoBg,
    border: ADMIN_COLORS.infoBorder,
    text: ADMIN_COLORS.infoText,
    icon: 'information-circle-outline',
  },
  warn: {
    bg: ADMIN_COLORS.warnBg,
    border: ADMIN_COLORS.warnBorder,
    text: ADMIN_COLORS.warnText,
    icon: 'warning-outline',
  },
};

const AdminAlert = ({ variant = 'info', children, icon, onClose, style }) => {
  const c = VARIANTS[variant] || VARIANTS.info;
  return (
    <View
      style={[styles.banner, { backgroundColor: c.bg, borderColor: c.border }, style]}
    >
      <Ionicons name={icon || c.icon} size={18} color={c.text} style={{ marginTop: 1 }} />
      <Text style={[styles.text, { color: c.text }]}>{children}</Text>
      {onClose ? (
        <Pressable onPress={onClose} hitSlop={8} style={styles.close}>
          <Ionicons name="close" size={16} color={c.text} />
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
  },
  text: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    fontFamily: ADMIN_FONT,
  },
  close: {
    marginTop: 1,
    marginLeft: 4,
  },
});

export default AdminAlert;
